import OrderedDocumentService from '@/services/ordered-document-service';
import type {
  AddToOrderRequest,
  AddToOrderResponse,
  BulkCreateOrderRequest,
  BulkCreateOrderResponse,
  DocumentOrderItem,
  GetOrderedDocumentsResponse,
  SuccessResponse,
} from '@/types/doc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

// Query keys
export const orderedDocumentKeys = {
  all: ['ordered-documents'] as const,
  lists: () => [...orderedDocumentKeys.all, 'list'] as const,
  list: (filters: any) => [...orderedDocumentKeys.lists(), filters] as const,
};

// Query Hooks
export const useOrderedDocuments = () => {
  return useQuery<GetOrderedDocumentsResponse>({
    queryKey: orderedDocumentKeys.lists(),
    queryFn: async () => {
      const response = await OrderedDocumentService.getOrdered();
      console.log(response);
      return response as GetOrderedDocumentsResponse;
    },
  });
};

interface UseAddToOrderOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useAddToOrder = ({
  onSuccess,
  successMessage,
}: UseAddToOrderOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation<AddToOrderResponse, Error, AddToOrderRequest>({
    mutationFn: async (request: AddToOrderRequest) => {
      const response = await OrderedDocumentService.addToOrder(request);
      return response.data as AddToOrderResponse;
    },
    onSuccess: () => {
      toast.success(successMessage ?? 'Document added to order successfully', {
        position: 'top-right',
      });
      queryClient.invalidateQueries({ queryKey: orderedDocumentKeys.lists() });
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error ||
        'Failed to add document to order. Please try again.';
      toast.error(errorMessage);
      console.error('Failed to add document to order:', error);
    },
  });
};

interface UseBulkCreateOrderOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useBulkCreateOrder = ({
  onSuccess,
  successMessage,
}: UseBulkCreateOrderOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation<BulkCreateOrderResponse, Error, BulkCreateOrderRequest>({
    mutationFn: async (requests: BulkCreateOrderRequest) => {
      const response = await OrderedDocumentService.bulkCreateOrder(requests);
      return response.data as BulkCreateOrderResponse;
    },
    onSuccess: () => {
      toast.success(successMessage ?? 'Documents added to order successfully', {
        position: 'top-right',
      });
      queryClient.invalidateQueries({ queryKey: orderedDocumentKeys.lists() });
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error ||
        'Failed to add documents to order. Please try again.';
      toast.error(errorMessage);
      console.error('Failed to add documents to order:', error);
    },
  });
};

interface UseBulkReorderDocumentsOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useBulkReorderDocuments = ({
  onSuccess,
  successMessage,
}: UseBulkReorderDocumentsOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation<SuccessResponse, Error, DocumentOrderItem[]>({
    mutationFn: async (documentOrders: DocumentOrderItem[]) => {
      const response = await OrderedDocumentService.bulkReorder(documentOrders);
      return response.data as SuccessResponse;
    },
    onSuccess: () => {
      toast.success(successMessage ?? 'Documents reordered successfully', {
        position: 'top-right',
      });
      queryClient.invalidateQueries({ queryKey: orderedDocumentKeys.lists() });
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.error ||
        'Failed to reorder documents. Please try again.';
      toast.error(errorMessage);
      console.error('Failed to reorder documents:', error);
    },
  });
};

interface UseRemoveFromOrderOptions {
  onSuccess?: () => void;
  successMessage?: string;
  onError?: () => void;
  onSettled?: () => void;
}

export const useRemoveFromOrder = ({
  onSuccess,
  successMessage,
  onError,
  onSettled,
}: UseRemoveFromOrderOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: async (orderedDocumentId: number) => {
      await OrderedDocumentService.deleteOrdered(orderedDocumentId);
    },
    onSuccess: () => {
      toast.success(
        successMessage ?? 'Document removed from order successfully',
        {
          position: 'top-right',
        }
      );
      queryClient.invalidateQueries({ queryKey: orderedDocumentKeys.lists() });
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.detail ||
        'Failed to remove document from order. Please try again.';
      toast.error(errorMessage);
      console.error('Failed to remove document from order:', error);
      onError?.();
    },
    onSettled: () => {
      onSettled?.();
    },
  });
};
